import { useCallback, useEffect, useState } from "react";

import { getPrescriptionOrders } from "../modules/refill/getPrescriptionOrders";
import { getBrandDisplayName } from "../modules/refill/getBrandDisplayName";

import { useAuthCredentials } from "./useAuthCredentials";

/**
 * Resolves the medication display name and image for a given prescription order.
 * Calls GET /v1/patient/{accessCode}/prescription_orders, finds the matching order
 * by orderId, and uses drugDisplayUrls[0] (from prescription_catalog.display_url)
 * and internalDrugIds[0] (from prescription_catalog.internal_drug_id) to populate
 * the header without requiring a separate prescriptions endpoint call.
 *
 * Used by PrescriptionFulfillmentDetailPage instead of useRefillConfirmation,
 * which crashes for async branded drugs (their fills link to PENDING prescriptions
 * with no prescription_catalog_id).
 */
export const useOrderMedication = (
  orderId: string | null
): {
  medicationName: string;
  medicationImageUrl: string | null;
  internalDrugId: string | null;
  loading: boolean;
  error: string | null;
} => {
  const { accessCode, accessToken } = useAuthCredentials();

  const [medicationName, setMedicationName] = useState("");
  const [medicationImageUrl, setMedicationImageUrl] = useState<string | null>(
    null
  );
  const [internalDrugId, setInternalDrugId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!accessCode) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const orders = await getPrescriptionOrders(accessCode, accessToken ?? "");
      const order = orderId
        ? orders.find((o) => o.orderId === orderId)
        : orders.reduce<(typeof orders)[number] | undefined>(
            (latest, o) =>
              !latest ||
              (o.dateCreated ?? "") > (latest.dateCreated ?? "")
                ? o
                : latest,
            undefined
          );
      const resolvedDrugId =
        order?.internalDrugIds.find((id) => getBrandDisplayName(id) !== null) ??
        null;
      setInternalDrugId(resolvedDrugId);
      setMedicationName(getBrandDisplayName(resolvedDrugId) ?? "");
      setMedicationImageUrl(
        order?.drugDisplayUrls.find((url) => url.length > 0) ?? null
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch order data"
      );
    } finally {
      setLoading(false);
    }
  }, [accessCode, accessToken, orderId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { medicationName, medicationImageUrl, internalDrugId, loading, error };
};
